import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Grid from "../../../../components/grid/Grid.js";
import { formatToBrowserTime } from "../../../../utils/timeFormat.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmarkCircle, faArrowLeft, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import EllipsedCell from "../../../../components/ellipsedCell/EllipsedCell.js";
import { Skeleton } from "@mui/material";
import { getDeploymentDetails, cancelDeployment } from "../../../../services/scm/TrackDeploymentAPI.js";
import "./TrackDeploymentForAssets.css"
import { toast } from "react-toastify";

const DeploymentField = ({ loading, value, formatTime = false }) => {
    if (loading) {
        return <Skeleton width={120} height={20} />;
    }
    
    if (!value) {
        return "—";
    }
    
    const displayValue = formatTime ? formatToBrowserTime(value, false, false) : value;
    return <EllipsedCell value={displayValue} />;
};

export default function TrackDeploymentForAssets() {
    const { t } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();

    const jobId = state?.jobId ?? "";

    const params = { jobId };

    const [selectedRows, setSelectedRows] = useState([]);
    const [deployments, setDeployments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [depLoading, setDepLoading] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [error, setError] = useState(null);

    // Top card state
    const [deploymentName, setDeploymentName] = useState("");
    const [description, setDescription] = useState("");
    const [createdAt, setCreatedAt] = useState("");        // Created At
    const [packageName, setPackageName] = useState("");
    const [version, setVersion] = useState("");
    const [deploymentStatus, setDeploymentStatus] = useState("");

    const headers = [
        { key: "thingName", label: "Thing Name", width: "14%" },
        { key: "retryAttempt", label: "Retry Attempt", width: "14%" },
        { key: "queuedAt", label: "Queued At", width: "19%" },
        { key: "startedAt", label: "Started At", width: "19%" },
        { key: "lastUpdatedAT", label: "Last Updated At", width: "19%" },
        { key: "status", label: "Status", width: "15%" },
    ];

    const mapApiResponseToGridData = (executionsArray) => {
        return (executionsArray ?? []).map((item, index) => {
            const thingArn = item?.thingArn ?? "";
            const thingName = thingArn ? thingArn.split("/").pop() : "";

            const summary = item?.jobExecutionSummary ?? {};
            const {
                status,
                queuedAt,
                startedAt,
                lastUpdatedAt,
                retryAttempt,
            } = summary;

            return {
                id: index + 1,
                thingName: thingName ?? "",
                retryAttempt: retryAttempt ?? "",
                queuedAt: queuedAt
                    ? formatToBrowserTime(queuedAt, false, false)
                    : "--",
                startedAt: startedAt
                    ? formatToBrowserTime(startedAt, false, false)
                    : "--",
                lastUpdatedAT: lastUpdatedAt
                    ? formatToBrowserTime(lastUpdatedAt, false, false)
                    : "--",
                status: status ?? "",
            };
        });
    };

    async function loadData(searchData) {
        try {
            setLoading(true);
            setDepLoading(true);

            const response = await getDeploymentDetails(searchData);
            const mapped = mapApiResponseToGridData(response.executions);
            setDeployments(mapped);

            const job = response.job ?? {};
            const {
                jobId,
                status,
                description,
                createdAt,
                packageName,
                versionName
            } = job;

            setDeploymentName(jobId ?? "");
            setDescription(description ?? "");
            setCreatedAt(createdAt ?? "");
            setDeploymentStatus(status ?? "");
            setPackageName(packageName ?? "");
            setVersion(versionName ?? "");
        } catch (err) {
            setError(err);
            console.error("error Fetching deployment details", err)
            navigate(-1);
        } finally {
            setLoading(false);
            setDepLoading(false);
        }
    }

    const handleRefreshClick = useCallback(async () => {
        if (!jobId) return;
        await loadData(params);
        setSelectedRows([]); // optional reset selection
    }, [jobId]);

    const handleSelectionChange = useCallback(async (rows) => {
        setSelectedRows(rows);
    }, []);

    const handleCancelClick = useCallback(async () => {
        if (!selectedRows.length) return;

        const thingNames = selectedRows.map((r) => r.thingName);
        const confirmMsg =
            thingNames.length === 1
                ? `Are you sure you want to cancel deployment for "${thingNames[0]}"?`
                : `Are you sure you want to cancel deployment for ${thingNames.length} devices?`;

        const confirmed = window.confirm(confirmMsg);
        if (!confirmed) return;

        try {
            setCancelLoading(true);

            const payload = {
                jobId: deploymentName || jobId,
                thingNames,
                forceCancel: true,
            };

            const response = await cancelDeployment(payload);
            toast.info(`Deployment canceled success for ${response.succeeded.length} targets and failed for ${response.failed.length} targets`);
            await loadData(params);
            setSelectedRows([]);
        } catch (err) {
            console.error("Error cancelling deployment:", err);
            toast.error("Something went wrong, Please try again.")
            setError(err);
        } finally {
            setCancelLoading(false);
        }
    }, [selectedRows, deploymentName, jobId]);


    useEffect(() => {
        if (!jobId) return;
        loadData(params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    return (
        <div className="container my-3 track-deployment-package h-100">
            <div className="col-12 col-xxl-10 responsive-col d-flex flex-column h-100">
                <div className="row mb-2">
                    <div className="d-flex align-items-center gap-0">
                        <button
                            className="btn btn-transperant"
                            onClick={() => navigate(-1)}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>

                        <h5 className="card-title mb-0">{t('Track Deployment for Assets')}</h5>
                    </div>
                </div>

                {/* Deployment Summary Card */}
                <div className="card mt-2 bg-light no-border">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-12 col-md-5 d-flex ad-field">
                                <div className="td-label">{t("Job Name")}:</div>
                                <div className="td-value custom-color">
                                    <DeploymentField loading={depLoading} value={deploymentName} />
                                </div>
                            </div>

                            <div className="col-12 col-md-4 d-flex ad-field">
                                <div className="td-label">{t("Created At")}:</div>
                                <div className="td-value">
                                    <DeploymentField loading={depLoading} value={createdAt} formatTime={true} />
                                </div>
                            </div>
                            <div className="col-12 col-md-3 d-flex ad-field">
                                <div className="td-label">{t("Status")}:</div>
                                <div className="td-value">
                                    <DeploymentField loading={depLoading} value={deploymentStatus} />
                                </div>
                            </div>

                            <div className="col-12 col-md-5 d-flex ad-field with-divider">
                                <div className="td-label">{t("Description")}:</div>
                                <div className="td-value">
                                    <DeploymentField loading={depLoading} value={description} />
                                </div>
                            </div>

                            <div className="col-12 col-md-4 d-flex ad-field with-divider">
                                <div className="td-label">{t("Package Name")}:</div>
                                <div className="td-value">
                                    <DeploymentField loading={depLoading} value={packageName} />
                                </div>
                            </div>

                            <div className="col-12 col-md-3 d-flex ad-field with-divider">
                                <div className="td-label">{t("Version")}:</div>
                                <div className="td-value ">
                                    <DeploymentField loading={depLoading} value={version} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions + selected rows count */}
                <div className="d-flex align-items-center mb-0">
                    <div className="me-auto">
                        {t(`Selected Rows (${selectedRows.length})`)}
                    </div>
                    <div className="action-buttons">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            disabled={
                                cancelLoading ||
                                selectedRows.length === 0
                            }
                            onClick={handleCancelClick}
                            title="Cancel"
                        >
                            {cancelLoading ? (
                                <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                />
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faXmarkCircle} className="me-1" />
                                    Cancel
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleRefreshClick}
                            disabled={loading || cancelLoading}
                            title="Refresh"
                        >
                            {loading ? (
                                <span className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faRotateRight} className="me-1" />
                                    Refresh
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Grid */}
                <div className="grid-wrapper">
                    <Grid
                        headers={headers}
                        data={deployments}
                        rowsPerPage={50}
                        canMultiSelect={true}
                        showPagination={true}
                        loading={loading}
                        onSelectionChange={handleSelectionChange}
                        sortable={true}
                    />
                </div>
            </div>
        </div>
    );
}
